<script setup lang="ts">
import { computed, ref } from 'vue'
import * as formatNumber from '@/helpers/numberFormatters'
import { getIntlState } from '@/lib/intl'

interface StudentObject {
  id: number
  name: string
  last_name?: string
  studied_course?: { title: string; course_id: number | null } | null
  smart_book?: { price?: number; purchased?: boolean } | null
  [key: string]: unknown
}

const props = defineProps<{
  studentId?: number | null
  studentObject?: StudentObject | null
}>()

defineEmits<{
  (e: 'update:student-id', value: null): void
  (e: 'open-login-modal'): void
}>()

const paymentModal = ref<{ open: (payload: unknown) => void } | null>(null)

const isIntl = computed(() => getIntlState())
const formatPrice = (val: number) => {
  if (getIntlState()) return formatNumber.arbitrary(val)
  return formatNumber.price.rub(val)
}

const doPurchase = () => {
  if (!paymentModal.value || !props.studentObject) return
  paymentModal.value.open({
    order: {
      name: `«Умный учебник» ${props.studentObject.studied_course?.title}`,
      price: props.studentObject.smart_book?.price
    },
    query: {
      student_ids: props.studentObject.id,
      course_id: props.studentObject.studied_course?.course_id
    }
  })
}
</script>

<template>
  <v-rich-modal
    title="Умный учебник"
    :model-value="!!studentId"
    @update:model-value="() => $emit('update:student-id', null)"
  >
    <v-payment-modal ref="paymentModal" url="/parent/request_purchase_smartbook" />

    <div v-if="studentId" class="student-smart-book-modal">
      <div class="mb-4">
        <p>
          Ребенок: <b>{{ studentObject?.last_name }} {{ studentObject?.name }}</b>
        </p>
        <div class="my-half" />
        <p>
          Текущий курс: <b>{{ studentObject?.studied_course?.title }}</b>
        </p>
      </div>

      <iframe
        class="black"
        width="560"
        height="315"
        src="https://www.youtube.com/embed/SVLxM-CqE4w"
        title="YouTube video player"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowfullscreen
      />

      <p v-if="studentObject?.smart_book?.purchased" class="mt-4">Войти в «Умный учебник» можно из кабинета ребёнка.</p>

      <p v-else class="text-title-bold d-flex mt-4 align-center">
        <span class="flex-spacer"> «Умный учебник» {{ studentObject?.studied_course?.title }} </span>

        {{ formatPrice(studentObject?.smart_book?.price ?? 0) }}
        <v-icon v-if="isIntl" class="ml-half">hand</v-icon>
      </p>
    </div>

    <template v-if="studentObject && studentObject.smart_book?.purchased" #actions>
      <div class="d-flex flex-column flex-spacer">
        <v-button mode="large warn block" target="_blank" :href="`/student/login_student/${studentId}`" class="mb-1">
          <v-icon class="mr-1">mdi-open-in-new</v-icon>
          Кабинет ученика
        </v-button>
        <v-button mode="large primary block" @click="() => $emit('open-login-modal')">
          <v-icon class="mr-1">mdi-cellphone</v-icon>
          Войти с другого устройства
        </v-button>
      </div>
    </template>

    <template v-else #actions>
      <v-button mode="primary filled large block" @click="doPurchase"> Купить </v-button>
    </template>
  </v-rich-modal>
</template>

<style lang="scss">
.student-smart-book-modal {
  @include desktop {
    width: min(100vw, 550px);
  }
}
</style>
